import { cn } from '@lib/utils';
import * as RCheckbox from '@radix-ui/react-checkbox';
import { Check, Minus } from 'lucide-react';
import { forwardRef, useId, type ComponentPropsWithoutRef, type ReactNode } from 'react';


export interface CheckboxProps
  extends Omit<ComponentPropsWithoutRef<typeof RCheckbox.Root>, 'onCheckedChange'> {
  label?: ReactNode;
  description?: ReactNode;
  onCheckedChange?: (checked: boolean) => void;
  containerClassName?: string;
}

export const Checkbox = forwardRef<HTMLButtonElement, CheckboxProps>(function Checkbox(
  { label, description, className, containerClassName, onCheckedChange, id, ...rest },
  ref,
) {
  const reactId = useId();
  const cid = id ?? reactId;

  return (
    <label htmlFor={cid} className={cn('flex items-start gap-3 cursor-pointer select-none', containerClassName)}>
      <RCheckbox.Root
        ref={ref}
        id={cid}
        onCheckedChange={(c) => onCheckedChange?.(c === true)}
        className={cn(
          'mt-0.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[5px]',
          'bg-apple-gray-200 transition-all duration-200 ease-apple',
          'hover:bg-apple-gray-300',
          'data-[state=checked]:bg-gradient-primary data-[state=checked]:shadow-btn-green data-[state=checked]:scale-110',
          'data-[state=indeterminate]:bg-gradient-primary data-[state=indeterminate]:shadow-btn-green',
          'focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-link/25',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          className,
        )}
        {...rest}
      >
        <RCheckbox.Indicator className="text-white">
          {rest.checked === 'indeterminate' ? (
            <Minus size={12} strokeWidth={3} />
          ) : (
            <Check size={12} strokeWidth={3} />
          )}
        </RCheckbox.Indicator>
      </RCheckbox.Root>

      {(label || description) && (
        <div className="flex flex-col">
          {label ? (
            <span className="text-[14px] font-medium text-apple-label leading-tight">{label}</span>
          ) : null}
          {description ? (
            <span className="mt-0.5 text-[12px] text-apple-gray-600 leading-snug">
              {description}
            </span>
          ) : null}
        </div>
      )}
    </label>
  );
});
